/**
 * Imports the API client and the PostsController module.
 * The API client is used to make HTTP requests to the backend API.
 * The PostsController module contains the endpoints for the posts-related API.
 */
import apiClient from '../apiClient';
import PostsController from '@/controllers/PostsController';

/**
 * Fetches a post by its unique identifier.
 *
 * @param {number} id - The ID of the post to fetch.
 * @returns {Promise<any>} - The response data containing the post details.
 */
export const getPostById = async (id) => {
  try {
    const response = await apiClient.get(`${PostsController.PostById}`, {
      params: { id }
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Fetches the trending posts from the API.
 *
 * @param {number} count - The number of trending posts to fetch.
 * @returns {Promise<any>} - The response data containing the trending posts.
 */
export const getTrendingPosts = async (count) => {
  try {
    const response = await apiClient.get(`${PostsController.TrendingPosts}`, {
      params: { count: 3 }
    });
    return response.data.data;
//console.log(response.data)
  } catch (error) {
    console.error('Error fetching trending posts:', error);
    throw error;
  }
};

/**
 * Fetch filtered posts based on user preferences and search criteria.
 * 
 * @param {number} userId - The ID of the user making the request.
 * @param {number} categoryId - The ID of the category to filter trending posts.
 * @param {string} searchTerm - The search term to filter trending posts.
 * @param {string} sortMethod - The method to sort the trending posts (e.g., 'date', 'popularity').
 * @param {number} pageIndex - The index of the current page of results.
 * @param {number} pageSize - The number of posts to fetch per page.
 * @returns {Promise<any>} - The response data containing the trending posts.
 */
export const filteredPosts = async ({
  userId, 
  categoryId, 
  searchTerm,
  sortMethod, 
  pageIndex, 
  pageSize
} = {}) => {  // Default to an empty object
  try {
    // Define query parameters
    const queryParams = {
      UserId: userId,
      CategoryId: categoryId,
      SearchTerm: searchTerm,
      SortMethod: sortMethod,
      PageIndex: pageIndex,
      PageSize: pageSize
    };

    // Filter out any undefined or null parameters
    Object.keys(queryParams).forEach(
      key => queryParams[key] === undefined && delete queryParams[key]
    );

    const response = await apiClient.get(`${PostsController.FilterPosts}`, {
      params: queryParams
    });
    return response;
  } catch (error) {
    console.error('Error fetching filtered posts:', error);
    throw error;
  }
};


/**
 * Fetches additional comments for a post.
 *
 * @param {number} PostId - The ID of the post to fetch more comments for.
 * @param {number} PageNumber - The page number of comments to fetch.
 * @returns {Promise<any>} - The response data containing the additional comments.
 */
export const loadMoreComments = async (PostId, PageNumber) => {
  try {
    const response = await apiClient.get(`${PostsController.LoadMoreComments}`, {
      params: { PostId, PageNumber }
    });
    return response.data;
  } catch (error) {
    console.error('Error loading more comments:', error);
    throw error;
  }
};
/**********New Services******************/
/**
 * Posts a new comment to a post.
 *
 * @param {Object} commentData - The data for the new comment to post.
 * @returns {Promise<any>} - The response data after posting the comment.
 */
export const postComment = async (commentData) => {
  try {
    const response = await apiClient.post('/Posts', commentData);
    return response.data;
  } catch (error) {
    console.log(error);
    throw error;
  }
};

/**
 * Deletes a post by its ID.
 *
 * @param {number} postId - The ID of the post to be deleted.
 * @returns {Promise<any>} - The response data after the post is deleted.
 */
export const deletePost = async (postId) => {
  try {
    const response = await apiClient.delete(`/Posts?id=${postId}`);
    return response.data;
  } catch (error) {
    console.log(error);
    throw error;
  }
};

/**
 * Edits a post by its ID.
 *
 * @param {number} postId - The ID of the post to be edited.
 * @param {Object} newComment - The updated data for the post.
 * @returns {Promise<any>} - The response data after the post is edited.
 */
export const editPost = async (postId, newComment) => {
  try {
    const response = await apiClient.put(`/Posts?id=${postId}`, newComment);
    return response.data;
  } catch (error) {
    if(error.response && error.response.data && error.response.data.isError){
        throw new Error(error.response.data.error.description)
    }else{
        // alert(error)
    }
  }
};

/**
 * Adds a new comment to a post.
 *
 * @param {Object} commentData - The data for the new comment to post.
 * @returns {Promise<any>} - The response data after posting the comment.
 */
export const addCommentOfPost = async (commentData) => {
  try {
    const response = await apiClient.post('/CommentsOfPosts', commentData);
    return response.data;
  } catch (error) {
    console.log(error);
    throw error;
  }
};

/**
 * Deletes a comment by its ID.
 *
 * @param {number} commentId - The ID of the comment to be deleted.
 * @returns {Promise<any>} - The response data after the comment is deleted.
 */
export const deleteCommentOfPost = async (commentId) => {
  try {
    const response = await apiClient.delete('/CommentsOfPosts', {
      params: { commentId } // Pass the commentId as a query parameter
    });
    return response.data;
  } catch (error) {
    console.log(error);
    throw error;
  }
};

/**
 * Edits a comment by its ID.
 *
 * @param {number} commentId - The ID of the comment to be edited.
 * @param {Object} newComment - The updated data for the comment.
 * @returns {Promise<any>} - The response data after the comment is edited.
 */
export const editCommentOfPost = async (commentId, newComment) => {
  try {
    const response = await apiClient.put(`CommentsOfPosts?commentId=${commentId}`, newComment);
    return response.data;
  } catch (error) {
    console.log(error);
    throw error;
  }
};
